import { useState } from "react";
import { Download, Upload, AlertTriangle, CheckCircle, Database, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";

export default function AdminDataMigration() {
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exportData, setExportData] = useState(null);
  const [message, setMessage] = useState(null);
  const [importResults, setImportResults] = useState(null);

  const handleExport = async () => {
    setExporting(true);
    setMessage(null);
    try {
      const { data } = await api.get("/admin/export-data");
      setExportData(data);
      // Auto-download as JSON file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `smart-english-data-export-${new Date().toISOString().split("T")[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      const totalItems = Object.values(data).reduce((sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0);
      setMessage({ type: "success", text: `Exported ${totalItems} items across ${Object.keys(data).length} collections. File downloaded!` });
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Export failed" });
    } finally {
      setExporting(false);
    }
  };

  const handleImportFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        setExportData(data);
        setMessage({ type: "success", text: `File loaded: ${Object.keys(data).length} collections found. Click "Import to This Server" to proceed.` });
      } catch (err) {
        setMessage({ type: "error", text: "Invalid JSON file. Please upload a valid export file." });
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleImport = async () => {
    if (!exportData) {
      setMessage({ type: "error", text: "No data to import. Export first or upload a file." });
      return;
    }
    if (!window.confirm("This will REPLACE all existing content (courses, books, chapters, etc.) with the imported data. User accounts and orders will NOT be affected. Continue?")) {
      return;
    }
    setImporting(true);
    setMessage(null);
    setImportResults(null);
    try {
      const { data } = await api.post("/admin/import-data", { data: exportData });
      setImportResults(data.imported);
      setMessage({ type: "success", text: data.message });
    } catch (err) {
      setMessage({ type: "error", text: err.response?.data?.detail || "Import failed" });
    } finally {
      setImporting(false);
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-data-migration-page">
        <h1 className="text-2xl font-black admin-text mb-1" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          Data Migration
        </h1>
        <p className="text-xs admin-text-secondary mb-6">Export content from preview and import to production (or vice versa)</p>

        {message && (
          <div className={`mb-4 p-4 rounded-lg flex items-start gap-3 ${message.type === "success" ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
            {message.type === "success" ? <CheckCircle size={18} className="text-green-600 flex-shrink-0 mt-0.5" /> : <AlertTriangle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />}
            <p className={`text-sm font-medium ${message.type === "success" ? "text-green-700" : "text-red-700"}`}>{message.text}</p>
          </div>
        )}

        {/* How it works */}
        <div className="admin-card border rounded-lg p-5 mb-6">
          <h2 className="text-sm font-bold admin-text mb-3 flex items-center gap-2">
            <Database size={16} className="text-[#0284C7]" /> How It Works
          </h2>
          <div className="space-y-2 text-xs admin-text-secondary">
            <p><strong>Step 1:</strong> On the SOURCE server (preview), click "Export All Data" to download a JSON file.</p>
            <p><strong>Step 2:</strong> On the DESTINATION server (production), upload the JSON file and click "Import to This Server".</p>
            <p className="text-amber-600 font-medium mt-2">Note: Import replaces all content data. User accounts and orders are NOT affected.</p>
          </div>
        </div>

        {/* Export Section */}
        <div className="admin-card border rounded-lg p-5 mb-4">
          <h2 className="text-sm font-bold admin-text mb-3 flex items-center gap-2">
            <Download size={16} className="text-green-600" /> Export Data
          </h2>
          <p className="text-xs admin-text-secondary mb-4">
            Downloads all courses, chapters, videos, books, e-books, banners, categories, notes, live sessions, and recordings as a JSON file.
          </p>
          <button
            data-testid="export-data-btn"
            onClick={handleExport}
            disabled={exporting}
            className="w-full bg-green-600 text-white font-bold py-3 text-sm rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {exporting ? <><RefreshCw size={16} className="animate-spin" /> Exporting...</> : <><Download size={16} /> Export All Data</>}
          </button>
        </div>

        {/* Import Section */}
        <div className="admin-card border rounded-lg p-5 mb-4">
          <h2 className="text-sm font-bold admin-text mb-3 flex items-center gap-2">
            <Upload size={16} className="text-[#0284C7]" /> Import Data
          </h2>
          <p className="text-xs admin-text-secondary mb-4">
            Upload a previously exported JSON file and import it to this server. This will replace all existing content.
          </p>

          {/* File Upload */}
          <label className="w-full border-2 border-dashed border-slate-300 rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer hover:border-[#0284C7] transition-colors mb-4" data-testid="import-file-upload">
            <Upload size={24} className="text-slate-400 mb-2" />
            <p className="text-xs text-slate-500 font-medium">Click to upload export JSON file</p>
            <input type="file" accept=".json" onChange={handleImportFile} className="hidden" />
          </label>

          {exportData && (
            <div className="bg-slate-50 rounded-lg p-3 mb-4">
              <p className="text-xs font-bold text-slate-700 mb-2">Data Ready to Import:</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {Object.entries(exportData).map(([key, val]) => (
                  <div key={key} className="text-[10px] text-slate-600">
                    <span className="font-medium">{key}:</span> {Array.isArray(val) ? val.length : 1} items
                  </div>
                ))}
              </div>
            </div>
          )}

          <button
            data-testid="import-data-btn"
            onClick={handleImport}
            disabled={importing || !exportData}
            className="w-full bg-[#0284C7] text-white font-bold py-3 text-sm rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {importing ? <><RefreshCw size={16} className="animate-spin" /> Importing...</> : <><Upload size={16} /> Import to This Server</>}
          </button>
        </div>

        {/* Import Results */}
        {importResults && (
          <div className="admin-card border border-green-200 rounded-lg p-5" data-testid="import-results">
            <h3 className="text-sm font-bold text-green-700 mb-3 flex items-center gap-2">
              <CheckCircle size={16} /> Import Complete
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {Object.entries(importResults).map(([key, count]) => (
                <div key={key} className="bg-green-50 rounded p-2 text-center">
                  <p className="text-lg font-bold text-green-700">{count}</p>
                  <p className="text-[10px] text-green-600 font-medium">{key}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
